// FoundersLeaderboard — public homepage widget surfacing the top
// Founders by Bazooka-redeemed invites. Turns the Chairman's Bazooka
// into a visible flex compounding the urgency of the "98 of 100
// founding spots left" banner, while keeping recipient identities
// fully anonymous (only first name + last initial of the Founder is
// shown, never the invitees).
//
// Renders nothing if the API returns an empty list — newly-deployed
// pods or environments without any Bazooka activity should NOT show
// an awkward empty card.
import React, { useEffect, useState } from "react";
import { Crown, Trophy, Sparkles } from "lucide-react";
import api from "../../lib/apiClient";

const FoundersLeaderboard = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    api.get("/invites/leaderboard?limit=10")
      .then((r) => { if (alive) setItems(r.data?.items || []); })
      .catch(() => { /* public endpoint, transient errors are non-actionable */ })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  // Hide the entire panel until we have at least one Founder with a
  // redeemed invite — empty leaderboards undermine the "this is happening"
  // narrative the panel is trying to convey.
  if (loading || items.length === 0) return null;

  // Medal cosmetic for top-3 — the rest of the list shows a small dot
  // so the eye doesn't get pulled below the leaders.
  const medal = (idx) => {
    if (idx === 0) return { icon: <Trophy className="h-3.5 w-3.5 text-amber-400" />, color: "text-amber-300" };
    if (idx === 1) return { icon: <Trophy className="h-3.5 w-3.5 text-slate-300" />, color: "text-slate-200" };
    if (idx === 2) return { icon: <Trophy className="h-3.5 w-3.5 text-orange-400" />, color: "text-orange-300" };
    return { icon: <Sparkles className="h-3 w-3 text-emerald-500/60" />, color: "text-gray-300" };
  };

  return (
    <section
      className="w-full mx-auto max-w-2xl px-4 sm:px-6 my-10 sm:my-14"
      data-testid="founders-leaderboard"
    >
      <header className="flex items-center gap-2 mb-4">
        <Crown className="h-5 w-5 text-amber-400" />
        <h2 className="text-base sm:text-lg font-semibold text-white tracking-tight">
          Top Founders
        </h2>
        <span className="text-xs text-gray-500 ml-2 hidden sm:inline">
          who've brought the most creators aboard
        </span>
      </header>
      <ol className="rounded-xl border border-gray-800 bg-gray-950/60 backdrop-blur divide-y divide-gray-800/70 overflow-hidden">
        {items.map((it, idx) => {
          const { icon, color } = medal(idx);
          // Anonymisation contract is enforced by the backend — we only
          // get first name + last-initial. The `username` field is null
          // unless the Founder explicitly opted into public listing.
          const display = it.last_initial
            ? `${it.first_name} ${it.last_initial}.`
            : it.first_name;
          // Conversion rate is sent_count > 0 by construction (any row
          // returned has at least 1 redeem, and redeems ≤ sends).
          const conv = it.sent_count > 0
            ? Math.round((it.redeemed_count / it.sent_count) * 100)
            : 100;
          return (
            <li
              key={`${it.first_name}-${it.last_initial || "x"}-${idx}`}
              className="flex items-center gap-3 px-4 py-3"
              data-testid={`founders-leaderboard-row-${idx}`}
            >
              <span className="w-5 text-center text-xs font-mono text-gray-500 shrink-0">
                {idx + 1}
              </span>
              <span className="shrink-0">{icon}</span>
              <span className={`flex-1 min-w-0 truncate text-sm font-medium ${color}`}>
                {display}
                {it.username && (
                  <span className="ml-2 text-[11px] text-gray-500 font-normal">
                    @{it.username}
                  </span>
                )}
              </span>
              <span className="text-sm font-semibold tabular-nums text-emerald-300">
                {it.redeemed_count}
                <span className="text-[10px] text-gray-500 ml-1 font-normal">redeemed</span>
              </span>
              <span className="hidden sm:inline text-[11px] tabular-nums text-gray-500 w-12 text-right">
                {conv}%
              </span>
            </li>
          );
        })}
      </ol>
      <p className="text-[11px] text-gray-500 mt-3 text-center">
        Become a Founder by inviting a creator yourself — the Chairman comps their first quarter.
      </p>
    </section>
  );
};

export default FoundersLeaderboard;
